"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Activity,
  Loader2,
  PauseCircle,
  PlayCircle,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { faNum, faDateTime, faToman, toFa } from "@/lib/format";
import type { SubscriptionWithRelations, SubscriptionStatus } from "./types";

type Props = { onRefresh: () => void };

function StatusBadge({ status }: { status: SubscriptionStatus | string }) {
  if (status === "ACTIVE") {
    return (
      <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
        فعال
      </Badge>
    );
  }
  if (status === "CANCELLED") {
    return (
      <Badge variant="destructive">لغو شده</Badge>
    );
  }
  return (
    <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400">
      منقضی شده
    </Badge>
  );
}

function daysBetween(a: string, b: string): number {
  const d = (Date.parse(b) - Date.parse(a)) / 86400000;
  return Math.round(d);
}

export default function SubscriptionsManager({ onRefresh }: Props) {
  const [subs, setSubs] = React.useState<SubscriptionWithRelations[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actingId, setActingId] = React.useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmTarget, setConfirmTarget] =
    React.useState<SubscriptionWithRelations | null>(null);
  const [confirmAction, setConfirmAction] =
    React.useState<"revoke" | "restore">("revoke");

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/subscriptions", {
        cache: "no-store",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت اشتراک‌ها");
      }
      setSubs(data.subs ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت اشتراک‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  async function act(id: string, action: "revoke" | "restore") {
    setActingId(id);
    try {
      const res = await fetch(`/api/admin/subscriptions?id=${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "عملیات ناموفق بود");
      }
      toast.success(action === "revoke" ? "اشتراک لغو شد." : "اشتراک فعال شد.");
      load();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "عملیات ناموفق بود";
      toast.error(msg);
    } finally {
      setActingId(null);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  }

  function openConfirm(s: SubscriptionWithRelations, action: "revoke" | "restore") {
    setConfirmTarget(s);
    setConfirmAction(action);
    setConfirmOpen(true);
  }

  const activeCount = subs.filter((s) => s.status === "ACTIVE").length;
  const expiredCount = subs.filter((s) => s.status !== "ACTIVE").length;

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="size-5 text-primary" />
              مدیریت اشتراک‌های شبکه خصوصی
            </CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
                فعال: {faNum(activeCount)}
              </Badge>
              <Badge variant="outline">کل: {faNum(subs.length)}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-11 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : subs.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              اشتراکی یافت نشد.
            </div>
          ) : (
            <div className="max-h-[32rem] overflow-y-auto scrollbar-thin">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead>کاربر</TableHead>
                    <TableHead>طرح</TableHead>
                    <TableHead>قیمت</TableHead>
                    <TableHead>وضعیت</TableHead>
                    <TableHead>منقضی</TableHead>
                    <TableHead>ترافیک</TableHead>
                    <TableHead>وضعیت دسترسی</TableHead>
                    <TableHead className="text-center">عملیات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.map((s) => {
                    const remaining = daysBetween(
                      new Date().toISOString(),
                      s.endDate,
                    );
                    const isVpn = !!s.user?.vpnAccess;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {s.user?.name ?? "—"}
                            </span>
                            <span
                              dir="ltr"
                              className="text-xs text-muted-foreground text-right"
                            >
                              {toFa(s.user?.phone ?? "—")}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-40">
                          <span
                            title={s.plan?.name}
                            className="block max-w-full truncate"
                          >
                            {s.plan?.name ?? "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {s.plan ? faToman(s.plan.priceToman) : "—"}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={s.status} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {faDateTime(s.endDate)}
                          {s.status === "ACTIVE" && (
                            <span
                              className={
                                remaining < 0
                                  ? "text-destructive"
                                  : remaining < 7
                                  ? "text-amber-600"
                                  : "text-emerald-600"
                            }
                            >
                              {" "}
                              ({remaining < 0
                                ? "منقضی شده"
                                : `${faNum(remaining)} روز`}
                              )
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {s.plan?.trafficGB
                            ? `${faNum(s.plan.trafficGB)} GB`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {isVpn ? (
                            <Badge className="border-sky-500/30 bg-sky-500/15 text-sky-600">
                              دسترسی VPN
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              بدون دسترسی
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {s.status === "ACTIVE" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              disabled={actingId === s.id}
                              onClick={() => openConfirm(s, "revoke")}
                            >
                              {actingId === s.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <PauseCircle className="size-3.5" />
                              )}
                              <span className="hidden sm:inline">
                                لغو
                              </span>
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary/90"
                              disabled={actingId === s.id}
                              onClick={() => openConfirm(s, "restore")}
                            >
                              {actingId === s.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <PlayCircle className="size-3.5" />
                              )}
                              <span className="hidden sm:inline">
                                فعال
                              </span>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail / revoke confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmAction === "revoke" ? (
                <PauseCircle className="size-5 text-destructive" />
              ) : (
                <PlayCircle className="size-5 text-primary" />
              )}
              {confirmAction === "revoke"
                ? "لغو اشتراک"
                : "بازگرداندن اشتراک"}
            </DialogTitle>
            <DialogDescription>
              {confirmTarget ? (
                <>
                  <div className="mt-1 flex flex-col gap-1 text-sm">
                    <span>
                      <span className="font-medium">کاربر:</span>{" "}
                      {confirmTarget.user?.name ?? "—"}
                    </span>
                    <span dir="ltr" className="text-right">
                      {toFa(confirmTarget.user?.phone ?? "—")}
                    </span>
                    <span>
                      <span className="font-medium">طرح:</span>{" "}
                      {confirmTarget.plan?.name ?? "—"}
                    </span>
                    <span>
                      <span className="font-medium">وضعیت سرور VPN:</span>{" "}
                      {confirmTarget.status === "ACTIVE"
                        ? "فعال"
                        : "غیرفعال"}
                    </span>
                    {confirmTarget.vlessLink && (
                      <span dir="ltr" className="break-all">
                        {confirmTarget.vlessLink}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                "در حال بارگذاری..."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
            >
              انصراف
            </Button>
            <Button
              variant={
                confirmAction === "revoke" ? "destructive" : "default"
              }
              disabled={!confirmTarget}
              onClick={() => {
                if (confirmTarget) act(confirmTarget.id, confirmAction);
              }}
            >
              {confirmAction === "revoke" ? "لغو اشتراک" : "فعال‌سازی"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
